import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import Icon from "../../components/AppIcon";

import PortfolioHero from "./components/PortfolioHero";
import CategoryFilter from "./components/CategoryFilter";
import ProjectGrid from "./components/ProjectGrid";
import ProjectModal from "./components/ProjectModal";
import PortfolioInteractive from "./components/PortfolioInteractive";

const CinematicShowcasePortfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = [
    { id: "all", name: "All Projects", icon: "Grid3X3" },
    { id: "corporate", name: "Corporate Brand Films", icon: "Building2" },
    { id: "executive", name: "Executive Portraits", icon: "User" },
    { id: "realestate", name: "Real Estate Cinematography", icon: "Home" },
    { id: "events", name: "Event Documentation", icon: "Calendar" },
    { id: "aerial", name: "Aerial Showcase", icon: "Plane" },
  ];

  const portfolioProjects = [
    {
      id: 1,
      title: "Fortune 500 Brand Transformation",
      category: "corporate",
      client: "TechCorp Industries",
      thumbnail:
        "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop",
      videoUrl:
        "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
      duration: "3:45",
      description: `A comprehensive brand transformation campaign that elevated TechCorp's market presence through cinematic storytelling. This project challenged us to capture the essence of innovation while maintaining corporate gravitas.

The creative approach focused on humanizing technology through authentic employee stories and cutting-edge visual techniques. We employed advanced lighting setups and precision camera movements to create a premium aesthetic that matched the client's elevated brand positioning.`,
      objectives:
        "Reposition brand as industry innovator, increase market credibility, attract top-tier talent",
      approach:
        "Cinematic documentary style with executive interviews and behind-the-scenes footage",
      technical:
        "RED Dragon 6K, Zeiss Master Prime lenses, custom lighting rig, drone cinematography",
      team: "Director, DP, Gaffer, Sound Engineer, Drone Pilot",
      outcomes:
        "40% increase in brand recognition, 25% boost in qualified job applications, featured in industry publications",
      behindScenes: [
        "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=400&h=300&fit=crop",
      ],
    },
    {
      id: 2,
      title: "CEO Leadership Portrait Series",
      category: "executive",
      client: "Global Financial Group",
      thumbnail:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop",
      videoUrl:
        "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
      duration: "2:30",
      description: `An intimate portrait series capturing the leadership philosophy of a Fortune 100 CEO. The challenge was creating authentic moments that revealed character while maintaining the gravitas expected of executive communications.

Our approach combined traditional portraiture with cinematic video elements, creating a multi-dimensional representation that serves both corporate communications and personal branding initiatives.`,
      objectives:
        "Humanize executive leadership, enhance personal brand, support thought leadership initiatives",
      approach:
        "Intimate interview style with environmental portraits showcasing leadership in action",
      technical:
        "Sony FX9, Canon L-series lenses, three-point lighting setup, wireless audio system",
      team: "Director, Portrait Photographer, Lighting Specialist, Audio Engineer",
      outcomes:
        "Featured in Harvard Business Review, 300% increase in LinkedIn engagement, speaking opportunities at major conferences",
      behindScenes: [
        "https://images.unsplash.com/photo-1556157382-97eda2d62296?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=300&fit=crop",
      ],
    },
    {
      id: 3,
      title: "Luxury Penthouse Showcase",
      category: "realestate",
      client: "Premier Properties",
      thumbnail:
        "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop",
      videoUrl:
        "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
      duration: "4:20",
      description: `A cinematic journey through Manhattan's most exclusive penthouse, showcasing architectural excellence and luxury lifestyle. The project required capturing both intimate details and sweeping vistas while maintaining the property's sophisticated atmosphere.

Technical challenges included managing natural light variations throughout the day and coordinating drone flights in restricted airspace. The result is a visual narrative that positions the property as more than real estate – it's a lifestyle statement.`,
      objectives:
        "Showcase architectural excellence, attract high-net-worth buyers, establish premium market positioning",
      approach:
        "Cinematic walkthrough with golden hour lighting and strategic drone reveals",
      technical:
        "DJI Inspire 2, Blackmagic Pocket Cinema Camera 6K, motorized slider system, professional lighting kit",
      team: "Director, Drone Pilot, Interior Photographer, Lighting Technician",
      outcomes:
        "Property sold 30% above asking price, featured in Architectural Digest, generated 2M+ social media impressions",
      behindScenes: [
        "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400&h=300&fit=crop",
      ],
    },
    {
      id: 4,
      title: "Global Product Launch Event",
      category: "events",
      client: "Innovation Labs",
      thumbnail:
        "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop",
      videoUrl:
        "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
      duration: "5:15",
      description: `Documentation of a high-stakes product launch that required capturing both the energy of live presentation and intimate behind-the-scenes moments. The challenge was maintaining cinematic quality while adapting to the unpredictable nature of live events.

Our multi-camera approach ensured comprehensive coverage while our discrete positioning allowed for authentic moments. Post-production focused on creating a narrative that builds excitement while showcasing the product's revolutionary features.`,
      objectives:
        "Document product launch, create promotional content, capture stakeholder testimonials",
      approach:
        "Multi-camera live event coverage with dedicated B-roll and interview segments",
      technical:
        "Canon C300 Mark III, wireless transmission system, professional audio mixing, LED panel lighting",
      team: "Director, Camera Operators (3), Audio Engineer, Lighting Director",
      outcomes:
        "Launch video reached 5M+ views, generated $2M in pre-orders, won industry event documentation award",
      behindScenes: [
        "https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=400&h=300&fit=crop",
      ],
    },
    {
      id: 5,
      title: "Coastal Development Aerial Survey",
      category: "aerial",
      client: "Oceanfront Developments",
      thumbnail:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
      videoUrl:
        "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
      duration: "3:10",
      description: `An ambitious aerial cinematography project showcasing a luxury coastal development from concept to completion. The project required coordinating multiple drone flights over 18 months to document construction progress while capturing the property's stunning natural setting.

Weather conditions and regulatory requirements presented ongoing challenges, requiring flexible scheduling and backup plans. The final piece demonstrates how aerial cinematography can transform real estate marketing by providing perspectives impossible with traditional photography.`,
      objectives:
        "Document development progress, showcase location advantages, create marketing materials",
      approach:
        "Time-lapse aerial documentation with seasonal variation and construction milestone captures",
      technical:
        "DJI Matrice 300 RTK, Zenmuse X7 camera, precision GPS mapping, weather monitoring systems",
      team: "Certified Drone Pilot, Aerial Cinematographer, Project Coordinator, Safety Officer",
      outcomes:
        "Pre-sales increased 60%, project featured in development industry publications, established new aerial documentation standards",
      behindScenes: [
        "https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop",
      ],
    },
    {
      id: 6,
      title: "Healthcare Innovation Campaign",
      category: "corporate",
      client: "MedTech Solutions",
      thumbnail:
        "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=600&fit=crop",
      videoUrl:
        "https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4",
      duration: "4:05",
      description: `A sensitive yet powerful campaign highlighting breakthrough medical technology while respecting patient privacy and regulatory requirements. The project balanced technical complexity with human impact, creating content that resonates with both medical professionals and patients.

Our approach emphasized authentic patient stories while showcasing the precision and innovation of the technology. Careful attention to lighting and composition created a premium aesthetic that reinforced the client's position as an industry leader.`,
      objectives:
        "Educate market about new technology, build trust with healthcare providers, demonstrate patient outcomes",
      approach:
        "Documentary-style storytelling with medical professional interviews and technology demonstrations",
      technical:
        "Sony FX6, medical-grade lighting, macro lenses for device detail, wireless audio for interviews",
      team: "Director, Medical Consultant, Lighting Specialist, Audio Engineer",
      outcomes:
        "Adoption rate increased 45%, featured at medical conferences, regulatory approval expedited",
      behindScenes: [
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=300&fit=crop",
        "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=300&fit=crop",
      ],
    },
  ];

  const filteredProjects =
    selectedCategory === "all"
      ? portfolioProjects
      : portfolioProjects.filter(
          (project) => project.category === selectedCategory,
        );

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
    document.body.style.overflow = "unset";
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      {/* <PortfolioHero /> */}
      <div data-theme="light">
        <PortfolioInteractive />
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Category Filter */}
        {/* <CategoryFilter 
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        /> */}

        {/* Projects Grid */}
        {/* <ProjectGrid 
          projects={filteredProjects}
          onProjectClick={openModal}
        /> */}

        {/* Call to Action */}
        <div className="mt-2 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-primary mb-2">
              Ready to Create Your
              <span className="text-red-600"> Cinematic Story</span>?
            </h2>
            <p className="text-xl text-text-secondary mb-2 sm:mb-4">
              Let's discuss how we can elevate your brand through precision
              cinematography and strategic visual storytelling.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking-concierge-premium-consultation"
                className="inline-flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-600-dark text-white px-8 py-2 rounded-lg font-semibold cinematic-transition cta-glow hover:shadow-elevation"
              >
                <Icon name="Calendar" size={20} strokeWidth={2} />
                <span>Schedule Consultation</span>
              </Link>
              <Link
                to="/corporate-solutions-services"
                className="inline-flex items-center justify-center space-x-2 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-8 py-2 rounded-lg font-semibold cinematic-transition"
              >
                <Icon name="Briefcase" size={20} strokeWidth={2} />
                <span>View Services</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Project Modal */}
      {/* {isModalOpen && selectedProject && (
        <ProjectModal 
          project={selectedProject}
          onClose={closeModal}
        />
      )} */}
    </div>
  );
};

export default CinematicShowcasePortfolio;
